import { supabase } from './supabase';
import { API_BASE } from './apiBase';

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export async function fetchAdminStats() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/stats`, { headers });
  if (!res.ok) throw new Error('Failed to fetch stats');
  return res.json();
}

export interface RevenueReport {
  rangeDays: number;
  todayRevenue: number;
  todayMargin: number;
  rangeRevenue: number;
  rangeMargin: number;
  revenueChart: { date: string; revenue: number; margin: number }[];
  bySource: { source: string; revenue: number; margin: number }[];
  estimatedRowCount: number;
  actualRowCount: number;
  generatedAt: string;
}

// Reporting seam — separate from /api/admin/stats (see revenueReportService).
export async function fetchRevenueReport(): Promise<RevenueReport> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/reports/revenue`, { headers });
  if (!res.ok) throw new Error('Failed to fetch revenue report');
  return res.json() as Promise<RevenueReport>;
}

export interface SurveySyncHealth {
  liveSurveys: number;
  closedSurveys: number;
  pausedSurveys: number;
  oldestSync: string | null;
  newestSync: string | null;
  syncedLastMinute: number;
  syncedLastTenMin: number;
  wouldBeGhosted: number;
  syncHealthy: boolean;
}

export async function fetchSurveySyncHealth(): Promise<SurveySyncHealth> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/survey-sync-health`, { headers });
  if (!res.ok) throw new Error('Failed to fetch survey sync health');
  return res.json();
}

export interface ExchangeHealthEntry {
  exchange_name: string;
  live_count: number;
  paused_count: number;
  closed_count: number;
  last_synced_at: string | null;
  synced_last_minute: number;
  total_completes_remaining: number;
  cpi_min: number | null;
  cpi_max: number | null;
  circuit_breakers_tripped: number;
  /** PS-specific: 's2s_firing' | 'workaround_active' | 'no_data' */
  s2s_status?: string;
}

export async function fetchExchangeHealth(): Promise<ExchangeHealthEntry[]> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/exchange-health`, { headers });
  if (!res.ok) throw new Error('Failed to fetch exchange health');
  return res.json();
}

// ── GoKart integration ───────────────────────────────────────────────────────
// Client-side mirrors of the api-local types (matches the ExchangeHealth /
// SurveySyncHealth convention of redeclaring rather than importing from shared).

export interface GokartDisplayConfig {
  /** null = ALL categories shown; an array = the whitelist. */
  displayCategories: string[] | null;
  /** FlatRate headline-points floor (does not apply to RevShare). */
  minDisplayPoints: number;
  showRevshare: boolean;
}

export interface GokartCategoryCount {
  category_name: string;
  live_count: number;
}

export interface GokartHealth {
  live_count: number;
  paused_count: number;
  closed_count: number;
  last_synced_at: string | null;
  synced_last_minute: number;
  /** True when GOKART_ENABLED is off — sync is intentionally dormant. The card
   *  shows a "Sync disabled" state instead of a stale-sync alarm. */
  sync_disabled: boolean;
  offers_live: number;
  games_live: number;
  conversions_pending: number;
  conversions_cleared: number;
  conversions_rejected: number;
  revenue_usd: number;
  points_paid: number;
  categories: GokartCategoryCount[];
  config: GokartDisplayConfig;
}

export async function fetchGokartHealth(): Promise<GokartHealth> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/gokart-health`, { headers });
  if (!res.ok) throw new Error('Failed to fetch GoKart health');
  return res.json();
}

/** Updates only the provided fields; returns the new effective config. */
export async function updateGokartConfig(partial: Partial<GokartDisplayConfig>): Promise<GokartDisplayConfig> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/gokart-config`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(partial),
  });
  if (!res.ok) throw new Error('Failed to update GoKart config');
  return res.json();
}

export async function fetchAdminMembers(page = 1, search = '', identityVerified?: 'true' | 'false') {
  const headers = await authHeaders();
  const params = new URLSearchParams({ page: String(page) });
  if (search) params.set('search', search);
  if (identityVerified !== undefined) params.set('identity_verified', identityVerified);
  const res = await fetch(`${API_BASE}/api/admin/members?${params}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch members');
  return res.json();
}

export interface MemberDemographics {
  age: { band: string; count: number }[];
  gender: { value: string; count: number }[];
  region: { value: string; count: number }[];
  hhi: { bracket: string; count: number }[];
  ethnicity: { value: string; count: number }[];
  total: number;
}

export async function fetchMemberDemographics(): Promise<MemberDemographics> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/demographics`, { headers });
  if (!res.ok) throw new Error('Failed to fetch member demographics');
  return res.json();
}

export async function fetchAdminMemberDetail(id: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${id}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch member detail');
  return res.json();
}

export async function adjustMemberPoints(id: string, delta: number, reason: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${id}/adjust-points`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ delta, reason }),
  });
  if (!res.ok) throw new Error('Failed to adjust points');
  return res.json();
}

export async function editAdminMember(id: string, data: { email?: string; display_name?: string; phone_number?: string; status?: string; role?: string; trust_score?: number; admin_notes?: string }) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update member');
  return res.json();
}

export async function deleteAdminMember(id: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error('Failed to delete member');
  return res.json();
}

export async function fetchAdminSurveys() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys`, { headers });
  if (!res.ok) throw new Error('Failed to fetch surveys');
  return res.json();
}

export async function fetchAdminConfig() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/config`, { headers });
  if (!res.ok) throw new Error('Failed to fetch config');
  return res.json();
}

export async function updateAdminConfig(key: string, value: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/config/${key}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw new Error('Failed to update config');
  return res.json();
}

export interface GeoBlocksResponse {
  total: number;
  by_country: { country: string; count: number; pct: number }[];
  daily_trend: { date: string; count: number }[];
}

export async function fetchGeoBlocks(days?: number): Promise<GeoBlocksResponse> {
  const headers = await authHeaders();
  const qs = days ? `?days=${days}` : '';
  const res = await fetch(`${API_BASE}/api/admin/geo-blocks${qs}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch geo blocks');
  return res.json() as Promise<GeoBlocksResponse>;
}

export async function fetchAdminBreakage() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/breakage`, { headers });
  if (!res.ok) throw new Error('Failed to fetch breakage');
  return res.json();
}

// ── Test Survey CRUD ──────────────────────────────────────────────────────

export interface TestSurveyQualification {
  attribute: string;
  operator?: string;
  values: string[];
  isPrescreen: boolean;
  prescreenQuestion?: string;
}

export interface TestSurveyQuota {
  attribute: string;
  values: { answer: string; target: number }[];
}

export interface TestSurveyPayload {
  title: string;
  category?: string;
  status: 'active' | 'paused' | 'draft';
  cpi: number;
  loi: number;
  ir: number;
  completesTarget: number;
  fieldEndDate?: string;
  qualifications: TestSurveyQualification[];
  quotas: TestSurveyQuota[];
}

export async function fetchTestSurveys() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys/test`, { headers });
  if (!res.ok) throw new Error('Failed to fetch test surveys');
  return res.json();
}

export async function fetchTestSurvey(id: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys/test/${id}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch test survey');
  return res.json();
}

export async function createTestSurvey(data: TestSurveyPayload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys/test`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed' }));
    throw new Error(err.error ?? 'Failed to create test survey');
  }
  return res.json();
}

export async function updateTestSurvey(id: string, data: TestSurveyPayload) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys/test/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update test survey');
  return res.json();
}

export async function deleteTestSurvey(id: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys/test/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error('Failed to delete test survey');
  return res.json();
}

export async function toggleTestSurveyStatus(id: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys/test/${id}/toggle-status`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) throw new Error('Failed to toggle status');
  return res.json();
}

export async function simulateTestSurvey(id: string, outcome: 'complete' | 'screenout' | 'quotafull' | 'security-terminate') {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/surveys/test/${id}/simulate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ outcome }),
  });
  if (!res.ok) throw new Error('Failed to simulate');
  return res.json();
}

export async function fetchEmailAnalytics() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/email-analytics`, { headers });
  if (!res.ok) throw new Error('Failed to fetch email analytics');
  return res.json();
}

export interface QualDiscoveryEntry {
  id: string;
  exchange: "ps" | "rfg";
  qual_name: string | null;
  qual_code: number | null;
  survey_count: number;
  suggested_attr_key: string | null;
  suggestion_confidence: "high" | "medium" | "low" | null;
  status: "unmapped" | "auto_mapped" | "confirmed" | "dismissed";
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  display_name: string;
  suggested_attr_display: string | null;
  action_description: string;
}

export interface CanonicalAttribute {
  key: string;
  label: string;
}

export async function fetchQualDiscoveryAttributes(): Promise<{ attributes: CanonicalAttribute[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/qual-discovery/attributes`, { headers });
  if (!res.ok) throw new Error("Failed to fetch canonical attributes");
  return res.json();
}

export async function fetchQualDiscovery(status?: string): Promise<{ entries: QualDiscoveryEntry[] }> {
  const headers = await authHeaders();
  const url = new URL(`${API_BASE}/api/admin/qual-discovery`);
  if (status) url.searchParams.set("status", status);
  const res = await fetch(url.toString(), { headers });
  if (!res.ok) throw new Error("Failed to fetch qual discovery");
  return res.json();
}

export async function patchQualDiscovery(id: string, body: { status?: QualDiscoveryEntry["status"]; attr_key?: string | null }): Promise<{ entry: QualDiscoveryEntry }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/qual-discovery/${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update qual discovery");
  return res.json();
}

// ── Qual Discovery — Phase 5 rebuild ─────────────────────────────────────────
// New shape consumed by the rebuilt admin page. Matches the /unmapped /
// /canonical-keys / /override endpoints added in Phase 5 Part D.

export interface QualSuggestion {
  canonical_key: string;
  label: string;
  confidence: number;
  reason: string;
  /**
   * True when the candidate's confidence meets the engine's
   * MIN_CONFIDENCE floor (0.30). Only confident candidates should be
   * pre-selected in the dropdown — weak candidates still appear for
   * context but the admin must deliberately pick them.
   */
  is_confident: boolean;
}

export interface QualDiscoveryUnmappedEntry {
  id: string;
  exchange: "purespectrum" | "rfg" | "cint";
  exchange_qual_id: string;
  question_text: string;
  options_preview: Array<{ code: string; label: string }>;
  active_survey_count: number;
  status: string;
  suggested_mappings: QualSuggestion[];
  current_override: {
    canonical_key: string;
    notes: string | null;
    updated_at: string;
  } | null;
}

export interface CanonicalKeyEntry {
  key: string;
  label: string;
  type: "single_select" | "multi_select" | "number" | "text" | "boolean" | "date";
  valid_values?: string[];
  description?: string;
}

export async function fetchQualDiscoveryUnmapped(): Promise<{ entries: QualDiscoveryUnmappedEntry[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/qual-discovery/unmapped`, { headers });
  if (!res.ok) throw new Error("Failed to fetch unmapped quals");
  return res.json();
}

export async function fetchCanonicalKeys(): Promise<{ entries: CanonicalKeyEntry[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/qual-discovery/canonical-keys`, { headers });
  if (!res.ok) throw new Error("Failed to fetch canonical keys");
  return res.json();
}

export async function saveQualMappingOverride(body: {
  exchange: QualDiscoveryUnmappedEntry["exchange"];
  exchange_qual_id: string;
  canonical_key: string;
  notes?: string;
}): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/qual-discovery/override`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: "Failed to save mapping" }));
    throw new Error(err.message ?? "Failed to save mapping");
  }
}

export async function deleteQualMappingOverride(
  exchange: QualDiscoveryUnmappedEntry["exchange"],
  exchangeQualId: string,
): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/admin/qual-discovery/override/${encodeURIComponent(exchange)}/${encodeURIComponent(exchangeQualId)}`,
    { method: "DELETE", headers },
  );
  if (!res.ok) throw new Error("Failed to remove mapping");
}

// ── Profile Intelligence ─────────────────────────────────────────────────────

export interface CoverageSnapshot {
  total_members: number;
  avg_attribute_count: number;
  source_breakdown: Record<string, number>;
  coverage_buckets: Record<'0-5' | '6-10' | '11-20' | '21+', number>;
  top_missing_attrs: Array<{
    attribute_key: string;
    surveys_requiring: number;
    members_having: number;
    gap_score: number;
  }>;
}

export interface CrossExchangeEntry {
  attribute_key: string;
  ps_qual_code: number | null;
  ps_survey_count: number;
  rfg_qual_names: string[];
  rfg_survey_count: number;
  member_coverage_pct: number;
  total_survey_count: number;
}

export interface AttributeWithProvenance {
  attribute_key: string;
  attribute_value: string;
  source: string;
  ps_qual_code: number | null;
  ps_condition_code: string | null;
  created_at: string;
  inference_reason: string | null;
  cross_exchange_value: number;
}

export interface InferenceChain {
  member_id: string;
  email: string | null;
  attributes: AttributeWithProvenance[];
}

export interface CustomProfileQuestion {
  id: string;
  question_text: string;
  attribute_key: string;
  attribute_label: string;
  question_type: 'buttons' | 'multi_select';
  options: Array<{ label: string; value: string }>;
  is_sensitive: boolean;
  min_trust_level: number;
  priority: number;
  exchanges: string[];
  target_filter: { attribute_key: string; attribute_value: string } | null;
  active: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export async function fetchCoverageSnapshot(): Promise<CoverageSnapshot> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/profile-intelligence/coverage`, { headers });
  if (!res.ok) throw new Error('Failed to fetch coverage');
  return res.json();
}

export async function fetchCrossExchangeMap(): Promise<{ entries: CrossExchangeEntry[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/profile-intelligence/cross-exchange-map`, { headers });
  if (!res.ok) throw new Error('Failed to fetch cross-exchange map');
  return res.json();
}

export async function fetchInferenceChain(memberIdOrEmail: string): Promise<InferenceChain> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/profile-intelligence/member/${encodeURIComponent(memberIdOrEmail)}/inference-chain`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed' }));
    throw new Error(err.error ?? 'Failed to fetch inference chain');
  }
  return res.json();
}

export async function fetchCustomQuestions(): Promise<{ entries: CustomProfileQuestion[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/profile-intelligence/custom-questions`, { headers });
  if (!res.ok) throw new Error('Failed to fetch custom questions');
  return res.json();
}

export async function createCustomQuestion(body: Partial<CustomProfileQuestion> & { question_text: string; attribute_key: string }): Promise<{ entry: CustomProfileQuestion }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/profile-intelligence/custom-questions`, {
    method: 'POST', headers, body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed' }));
    throw new Error(err.error ?? 'Failed to create question');
  }
  return res.json();
}

export async function patchCustomQuestion(id: string, body: Partial<CustomProfileQuestion>): Promise<{ entry: CustomProfileQuestion }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/profile-intelligence/custom-questions/${id}`, {
    method: 'PATCH', headers, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update question');
  return res.json();
}

export async function deleteCustomQuestion(id: string): Promise<{ entry: CustomProfileQuestion }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/profile-intelligence/custom-questions/${id}`, {
    method: 'DELETE', headers,
  });
  if (!res.ok) throw new Error('Failed to delete question');
  return res.json();
}

// ── Fraud Detection ─────────────────────────────────────────────────────────

export async function fetchFraudOverview() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/fraud/overview`, { headers });
  if (!res.ok) throw new Error('Failed to fetch fraud overview');
  return res.json();
}

export async function fetchFraudFlaggedMembers(page = 1, limit = 25, tier?: string) {
  const headers = await authHeaders();
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (tier) params.set('tier', tier);
  const res = await fetch(`${API_BASE}/api/admin/fraud/flagged-members?${params}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch flagged members');
  return res.json();
}

export async function fetchFraudSignalFeed() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/fraud/signal-feed`, { headers });
  if (!res.ok) throw new Error('Failed to fetch signal feed');
  return res.json();
}

export async function fetchFraudDeviceClusters() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/fraud/device-clusters`, { headers });
  if (!res.ok) throw new Error('Failed to fetch device clusters');
  return res.json();
}

export async function fetchFraudIpClusters() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/fraud/ip-clusters`, { headers });
  if (!res.ok) throw new Error('Failed to fetch IP clusters');
  return res.json();
}

export async function fetchFraudMemberDetail(memberId: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/fraud/members/${memberId}/detail`, { headers });
  if (!res.ok) throw new Error('Failed to fetch fraud member detail');
  return res.json();
}

export async function setFraudTier(memberId: string, tier: string, reason: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${memberId}/fraud-tier`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tier, reason }),
  });
  if (!res.ok) throw new Error('Failed to set fraud tier');
  return res.json();
}

// ── Member Detail Tabs ──────────────────────────────────────────────────────

export async function adjustMemberTrustScore(memberId: string, adjustment: number, reason: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${memberId}/trust-score`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ adjustment, reason }),
  });
  if (!res.ok) throw new Error('Failed to adjust trust score');
  return res.json();
}

export async function fetchMemberAttributes(memberId: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${memberId}/attributes`, { headers });
  if (!res.ok) throw new Error('Failed to fetch member attributes');
  return res.json();
}

export async function upsertMemberAttribute(memberId: string, attribute_key: string, attribute_value: string, notes: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${memberId}/attributes`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ attribute_key, attribute_value, notes }),
  });
  if (!res.ok) throw new Error('Failed to upsert attribute');
  return res.json();
}

export async function fetchMemberSessions(memberId: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${memberId}/sessions`, { headers });
  if (!res.ok) throw new Error('Failed to fetch member sessions');
  return res.json();
}

export async function fetchMemberTrustHistory(memberId: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/members/${memberId}/trust-history`, { headers });
  if (!res.ok) throw new Error('Failed to fetch trust history');
  return res.json();
}

// ── Buyer Quality ───────────────────────────────────────────────────────────

export async function fetchBuyerQualityOverview() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/buyer-quality/overview`, { headers });
  if (!res.ok) throw new Error('Failed to fetch buyer quality overview');
  return res.json();
}

export async function fetchBuyerQualityBuyers(page = 1, limit = 25, exchange?: string, suppressed?: string) {
  const headers = await authHeaders();
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (exchange) params.set('exchange', exchange);
  if (suppressed) params.set('suppressed', suppressed);
  const res = await fetch(`${API_BASE}/api/admin/buyer-quality/buyers?${params}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch buyers');
  return res.json();
}

export async function suppressBuyer(exchange: string, buyerId: string, hours: number, reason: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/buyer-quality/buyers/${exchange}/${buyerId}/suppress`, {
    method: 'POST', headers, body: JSON.stringify({ hours, reason }),
  });
  if (!res.ok) throw new Error('Failed to suppress buyer');
  return res.json();
}

export async function unsuppressBuyer(exchange: string, buyerId: string) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/buyer-quality/buyers/${exchange}/${buyerId}/unsuppress`, {
    method: 'POST', headers,
  });
  if (!res.ok) throw new Error('Failed to unsuppress buyer');
  return res.json();
}

// ── Targeting Simulator ─────────────────────────────────────────────────────

export interface SimulatorProfile {
  age: number;
  gender: 'male' | 'female' | 'other';
  zip_code: string;
  hhi?: number;
  education?: string;
  employment_status?: string;
  hispanic_origin?: boolean;
  race_ethnicity?: string;
  has_children?: boolean;
  child_count?: number;
  language_at_home?: string;
  pet_ownership?: string;
  home_ownership?: string;
  auto_make?: string;
  voter_registration?: boolean;
  political_party?: string;
  industry?: string;
  cell_plan?: string;
  ailments?: string[];
  insurance_products?: string[];
  social_media_use?: string[];
  car_count?: number;
  movies_frequency?: string;
}

export interface SimulatorQualResult {
  attr_key: string;
  qual_name: string;
  required_values: string;
  member_value: string | null;
  result: 'pass' | 'fail' | 'unknown';
}

export interface SimulatorSurveyResult {
  survey_id: string;
  exchange: 'ps' | 'rfg';
  title: string;
  cpi: number;
  loi: number;
  ir: number;
  reason_included?: string;
  reason_excluded?: string;
  quals_passed: SimulatorQualResult[];
  quals_failed: SimulatorQualResult[];
  quals_unknown: SimulatorQualResult[];
}

export interface SimulatorResult {
  summary: {
    total_live: number;
    total_shown: number;
    total_excluded: number;
    ps_shown: number;
    rfg_shown: number;
    ps_excluded: number;
    rfg_excluded: number;
  };
  shown: SimulatorSurveyResult[];
  excluded: SimulatorSurveyResult[];
}

export async function simulateTargeting(profile: SimulatorProfile): Promise<SimulatorResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/simulator`, {
    method: 'POST',
    headers,
    body: JSON.stringify(profile),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Simulation failed' }));
    throw new Error(err.error ?? 'Simulation failed');
  }
  return res.json();
}

// ─── Buyer-Demand Phase 1: buyers + manual projects ────────────────────────

export interface BuyerSummary {
  id: string;
  name: string;
  contact_email: string;
  contact_name: string | null;
  notes: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  _count?: { exchange_surveys: number };
}

export interface ManualProject {
  id: string;
  exchange_name: string;
  exchange_survey_id: string;
  survey_name: string;
  survey_status: string;
  status: 'LIVE' | 'PAUSED' | 'CLOSED' | null;
  cpi: number;
  loi: number;
  ir: number;
  completes_remaining: number;
  category: string | null;
  entry_url: string | null;
  buyer_id: string | null;
  created_at: string;
  updated_at: string;
  buyer?: { id: string; name: string; contact_email: string; status: string } | null;
}

export async function fetchBuyers(): Promise<{ buyers: BuyerSummary[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/buyers`, { headers });
  if (!res.ok) throw new Error('Failed to fetch buyers');
  return res.json();
}

export async function fetchBuyer(id: string): Promise<{ buyer: BuyerSummary }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/buyers/${encodeURIComponent(id)}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch buyer');
  return res.json();
}

export async function createBuyer(input: {
  name: string;
  contact_email: string;
  contact_name?: string;
  notes?: string;
}): Promise<{ buyer: BuyerSummary }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/buyers`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to create buyer' }));
    throw new Error(err.error ?? 'Failed to create buyer');
  }
  return res.json();
}

export async function updateBuyer(
  id: string,
  input: Partial<{ name: string; contact_email: string; contact_name: string | null; notes: string | null; status: 'active' | 'paused' | 'archived' }>,
): Promise<{ buyer: BuyerSummary }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/buyers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to update buyer' }));
    throw new Error(err.error ?? 'Failed to update buyer');
  }
  return res.json();
}

export async function fetchManualProjects(buyerId?: string): Promise<{ projects: ManualProject[] }> {
  const headers = await authHeaders();
  const url = buyerId
    ? `${API_BASE}/api/admin/manual-projects?buyer_id=${encodeURIComponent(buyerId)}`
    : `${API_BASE}/api/admin/manual-projects`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('Failed to fetch projects');
  return res.json();
}

// Cross-buyer projects table — same endpoint as fetchManualProjects but
// returns the financial-rollup shape the admin operations view needs.
// Kept distinct from ManualProject so call sites that only need the
// thin shape (AdminProjectForm, AdminBuyerDetail link) aren't forced to
// thread through `revenue_usd` etc.
export interface ProjectSummary extends ManualProject {
  total_starts: number;
  total_completes: number;
  total_incentive_points: number;
  revenue_usd: number;
  incentive_usd: number;
  margin_usd: number;
}

export async function fetchProjects(
  buyerId?: string,
): Promise<{ projects: ProjectSummary[] }> {
  const headers = await authHeaders();
  const url = buyerId
    ? `${API_BASE}/api/admin/manual-projects?buyer_id=${encodeURIComponent(buyerId)}`
    : `${API_BASE}/api/admin/manual-projects`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('Failed to fetch projects');
  return res.json();
}

export async function fetchManualProject(id: string): Promise<{ project: ManualProject }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/manual-projects/${encodeURIComponent(id)}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch project');
  return res.json();
}

export async function createManualProject(input: {
  buyer_id: string;
  reference: string;
  country?: string;
  language?: string;
  completes: number;
  cpi: number;
  loi: number;
  ir: number;
  entry_url: string;
  category?: string;
  status?: 'LIVE' | 'PAUSED';
}): Promise<{ project: ManualProject }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/manual-projects`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to create project' }));
    throw new Error(err.error ?? 'Failed to create project');
  }
  return res.json();
}

// ─── Buyer-Demand Phase 3: quota cells ─────────────────────────────────────

export interface QuotaCell {
  quota_key: string;          // generated client-side via buildQuotaKey
  constraints: Record<string, string>;
  target: number;
}

export interface QuotaProgressRow {
  quota_key: string;
  constraints: Record<string, string>;
  filled: number;
  target: number;
  pct_filled: number;
}

export async function fetchQuotaProgress(id: string): Promise<{ progress: QuotaProgressRow[] }> {
  const headers = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/admin/manual-projects/${encodeURIComponent(id)}/quota-progress`,
    { headers },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to fetch quota progress' }));
    throw new Error(err.error ?? 'Failed to fetch quota progress');
  }
  return res.json();
}

// ─── Buyer-Demand Phase 4: project stats panel ─────────────────────────────

export interface ProjectStats {
  in_flight: number;
  total_starts: number;
  total_completes: number;
  total_quota_full: number;
  total_screener_fail: number;
  conversion_rate: number;
  completions_by_day: Array<{ date: string; count: number }>;
  median_completion_minutes: number | null;
}

export async function fetchProjectStats(id: string): Promise<ProjectStats> {
  const headers = await authHeaders();
  const res = await fetch(
    `${API_BASE}/api/admin/manual-projects/${encodeURIComponent(id)}/stats`,
    { headers },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to fetch project stats' }));
    throw new Error(err.error ?? 'Failed to fetch project stats');
  }
  return res.json();
}

export async function updateManualProject(
  id: string,
  input: Partial<{
    reference: string;
    completes: number;
    cpi: number;
    loi: number;
    ir: number;
    entry_url: string;
    category: string | null;
    status: 'LIVE' | 'PAUSED' | 'CLOSED';
    qualifications: ManualQualEntry[];
    quotas: QuotaCell[];
  }>,
): Promise<{ project: ManualProject }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/manual-projects/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to update project' }));
    throw new Error(err.error ?? 'Failed to update project');
  }
  return res.json();
}

// ─── Buyer-Demand Phase 2: canonical attribute picker + feasibility ────────

export type AttributeCategory =
  | 'demographics' | 'geography' | 'household' | 'employment' | 'education'
  | 'health' | 'auto' | 'consumer_behavior' | 'political' | 'lifestyle'
  | 'b2b' | 'custom';

export type AttributeInputType =
  | 'single_select' | 'multi_select' | 'number' | 'text' | 'boolean' | 'date';

export interface CanonicalAttributeView {
  key: string;
  label: string;
  category: AttributeCategory;
  inputType: AttributeInputType;
  options?: Array<{ value: string; label: string }>;
  memberCount: number;
  b2b: boolean;
  derived: boolean;
  deprecated: boolean;
}

export type ManualMatchRule =
  | { kind: 'choice'; values: string[] }
  | { kind: 'range'; min?: number; max?: number }
  | { kind: 'multi_select'; values: string[]; require_any?: boolean }
  // Phase 2.5 — date_of_birth's dedicated rule. Picker emits this
  // (instead of generic `range`) so the matcher can compute age from
  // the canonical ISO-date storage. Boundaries inclusive on both ends.
  | { kind: 'age_range'; minAge: number; maxAge: number };

export interface AttrQualification {
  attributeKey: string;
  match: ManualMatchRule;
}

export interface CustomQuestion {
  kind: 'custom_question';
  question: string;
  type: 'single' | 'multi';
  options: string[];
  required: string[];
  search_hash: string;
  promoted: false;
}

export type ManualQualEntry = AttrQualification | CustomQuestion;

export async function fetchCanonicalAttributes(): Promise<{
  attributes: CanonicalAttributeView[];
  byCategory: Record<string, CanonicalAttributeView[]>;
}> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/canonical-attributes`, { headers });
  if (!res.ok) throw new Error('Failed to fetch canonical attributes');
  return res.json();
}

export async function fetchFeasibility(qualifications: ManualQualEntry[]): Promise<{
  panelMatch: number;
  totalActiveMembers: number;
}> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/manual-projects/feasibility`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ qualifications }),
  });
  if (!res.ok) throw new Error('Failed to compute feasibility');
  return res.json();
}

// ── Member care chat — admin views ─────────────────────────────────────────

export interface SupportChatListRow {
  id: string;
  member_id: string;
  issue_type: string;
  summary: string;
  resolution: string;
  points_granted: number;
  turn_count: number;
  session_id: string | null;
  created_at: string;
  closed_at: string | null;
  member: {
    id: string;
    email: string | null;
    display_name: string | null;
    trust_score: number;
  };
}

export interface SupportChatListResponse {
  chats: SupportChatListRow[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export async function fetchSupportChats(params: {
  page?: number;
  limit?: number;
  resolution?: string;
  issue_type?: string;
  member_id?: string;
} = {}): Promise<SupportChatListResponse> {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.resolution) qs.set('resolution', params.resolution);
  if (params.issue_type) qs.set('issue_type', params.issue_type);
  if (params.member_id) qs.set('member_id', params.member_id);
  const res = await fetch(`${API_BASE}/api/admin/support-chats?${qs.toString()}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch support chats');
  return res.json();
}

export interface SupportChatDetail extends SupportChatListRow {
  member: SupportChatListRow['member'] & {
    total_points: number;
    status: string;
    created_at: string;
  };
}

export async function fetchSupportChatDetail(id: string): Promise<{ chat: SupportChatDetail }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/support-chats/${id}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch support chat');
  return res.json();
}

// ── Care-Rep Ticket System (commit 3/4 — read-only console) ────────────────

export type TicketStatus = 'open' | 'contacted' | 'resolved' | 'closed';
export type TicketSource = 'lockout' | 'turn_cap' | 'haiku_escalation' | 'verification_failed';

export interface TicketMember {
  id: string;
  email: string | null;
  display_name: string | null;
  trust_score: number;
  total_points: number;
  status: string;
}

export interface TicketListRow {
  id: string;
  member_id: string;
  status: TicketStatus;
  source: TicketSource;
  issue_type: string;
  summary: string;
  claim_outcome: string | null;
  escalation_reason: string | null;
  owed_amount: number | null;
  assignee_id: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  _count: { messages: number };
  member: TicketMember;
}

export interface TicketListResponse {
  tickets: TicketListRow[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export async function fetchTickets(params: {
  page?: number;
  limit?: number;
  status?: string;
  source?: string;
} = {}): Promise<TicketListResponse> {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.status) qs.set('status', params.status);
  if (params.source) qs.set('source', params.source);
  const res = await fetch(`${API_BASE}/api/admin/tickets?${qs.toString()}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch tickets');
  return res.json();
}

export interface TicketTranscriptTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface TicketAuditRow {
  id: string;
  ticket_id: string;
  actor_id: string;
  from_status: TicketStatus | null;
  to_status: TicketStatus;
  reason: string | null;
  created_at: string;
}

export interface TicketMessageRow {
  id: string;
  ticket_id: string;
  channel: 'email' | 'in_app';
  direction: 'outbound' | 'inbound';
  body: string;
  actor_id: string;
  created_at: string;
}

export interface TicketDetail extends Omit<TicketListRow, '_count' | 'member'> {
  transcript: TicketTranscriptTurn[] | null;
  member: TicketMember & { created_at: string };
  audits: TicketAuditRow[];
  messages: TicketMessageRow[];
}

export interface TicketAward {
  awarded: boolean;
  amount: number;
}

export async function fetchTicketDetail(id: string): Promise<{ ticket: TicketDetail; award: TicketAward }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/tickets/${id}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch ticket');
  return res.json();
}

// ── Ticket resolution actions (commit 4/4 — money path) ────────────────────

export async function awardTicket(id: string, amount: number, reason: string): Promise<{ awarded: number; new_balance: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/tickets/${id}/award`, {
    method: 'POST', headers, body: JSON.stringify({ amount, reason }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? 'Failed to award points');
  return res.json();
}

export async function contactTicketMember(id: string, body: string): Promise<{ ok: boolean; email_sent: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/tickets/${id}/contact`, {
    method: 'POST', headers, body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? 'Failed to send message');
  return res.json();
}

export async function transitionTicket(id: string, to: TicketStatus, reason?: string): Promise<{ applied: boolean; status: TicketStatus }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/tickets/${id}/transition`, {
    method: 'POST', headers, body: JSON.stringify({ to, ...(reason ? { reason } : {}) }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? 'Failed to update status');
  return res.json();
}

// ---------------------------------------------------------------------------
// Redemption review console (build seq #4, Pass 4c). Types mirror the Pass-4b
// read API + Pass-4a transition routes exactly. /api/admin/redemptions/*.
// ---------------------------------------------------------------------------

export type RiskBandValue = 'low' | 'medium' | 'high';
export type RowAction = 'approve' | 'hold' | 'reject';
export type Trajectory = 'up' | 'down' | 'flat';

export interface RiskSignal {
  name: string;
  value: string | number;
  contribution: RiskBandValue;
  detail?: string;
}

// AI-assist (build seq #5): the cached cross-signal recommendation. The AI can
// only ever recommend caution or stay silent — there is no 'approve' value.
export interface AiRecommendationView {
  recommendation: 'hold' | 'escalate' | 'none';
  reasoning: string;
}

export interface QueueRow {
  redemption: {
    id: string;
    status: string;
    points_cost: number;
    usd_value: number;
    sku: string | null;
    brand: string | null;
    created_at: string;
    risk_band: RiskBandValue | null;
  };
  member: {
    id: string;
    display_name: string | null;
    trust_tier: number;
    trust_tier_name: string;
    trust_trajectory: Trajectory;
    account_age_days: number;
  };
  risk: {
    band: RiskBandValue;
    reason: string;
    signals: RiskSignal[];
    computedAt: string | null;
    cached: boolean;
    ai?: AiRecommendationView | null;
  };
  actions: RowAction[];
  batch_eligible: boolean;
}

export interface QueuePage {
  rows: QueueRow[];
  total: number;
  limit: number;
  offset: number;
  nextOffset: number | null;
}

export interface QueueCounts {
  high: number;
  medium: number;
  low: number;
  uncomputed: number;
  total: number;
}

export interface RedemptionDetail {
  redemption: Record<string, unknown> & { id: string; status: string; points_cost: number; usd_value: number; vi_sku: string | null; created_at: string; risk_band: RiskBandValue | null };
  member: {
    id: string;
    display_name: string | null;
    email: string | null;
    trust_score: number;
    fraud_score: number;
    fraud_tier: string;
    total_points: number;
    created_at: string;
    status: string;
    trust_tier: number;
    trust_tier_name: string;
    account_age_days: number;
  } | null;
  risk: { band: RiskBandValue; reason: string; signals: RiskSignal[]; floor: RiskBandValue; autoReleaseEligible: boolean } | null;
  ai: AiRecommendationView | null;
  actions: RowAction[];
  earn_summary: { lifetime_earned: number; lifetime_redeemed: number; earn_to_redeem_ratio: number | null };
  redemption_history: Array<{ id: string; status: string; points_cost: number; usd_value: number; vi_sku: string | null; created_at: string; risk_band: RiskBandValue | null }>;
  sessions: Array<{ session_id: string; exchange_name: string; survey_name: string; status: string; points_credited: number | null; started_at: string; completed_at: string | null }>;
  support_chats: {
    total_grants: number;
    chat_count: number;
    recent: Array<{ id: string; issue_type: string; resolution: string; points_granted: number; summary: string; session_id: string | null; created_at: string }>;
  };
  trust_log: Array<{ delta: number; score_after: number; reason: string; source_type: string; created_at: string }>;
  devices: {
    distinct_fingerprints: number;
    recent: Array<{ fingerprint_hash: string; first_seen_at: string; last_seen_at: string }>;
  };
  audit_trail: Array<{ actor_id: string; role_at_action: string | null; from_status: string; to_status: string; risk_band_at_action: RiskBandValue | null; reason: string | null; ai_recommendation: string | null; followed_ai: boolean | null; created_at: string }>;
}

export interface ActionResult {
  success: boolean;
  redemptionId: string;
  status: string;
}

export interface BatchApproveResult {
  success: boolean;
  requested: number;
  approved: number;
  skipped: number;
  results: Array<{ id: string; ok: boolean; status?: string; error?: string }>;
}

export async function fetchRedemptionQueue(opts: { limit?: number; offset?: number } = {}): Promise<QueuePage> {
  const headers = await authHeaders();
  const params = new URLSearchParams();
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.offset != null) params.set('offset', String(opts.offset));
  const qs = params.toString();
  const res = await fetch(`${API_BASE}/api/admin/redemptions/queue${qs ? `?${qs}` : ''}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch redemption queue');
  return res.json();
}

export async function fetchRedemptionQueueCounts(): Promise<QueueCounts> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/redemptions/queue/counts`, { headers });
  if (!res.ok) throw new Error('Failed to fetch redemption queue counts');
  return res.json();
}

export async function fetchRedemptionDetail(id: string): Promise<RedemptionDetail> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/redemptions/${id}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch redemption detail');
  return res.json();
}

export async function approveRedemption(id: string): Promise<ActionResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/redemptions/${id}/approve`, { method: 'POST', headers, body: '{}' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to approve redemption' }));
    throw new Error(err.error ?? 'Failed to approve redemption');
  }
  return res.json();
}

export async function holdRedemption(id: string, reason: string): Promise<ActionResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/redemptions/${id}/hold`, { method: 'POST', headers, body: JSON.stringify({ reason }) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to hold redemption' }));
    throw new Error(err.error ?? 'Failed to hold redemption');
  }
  return res.json();
}

export async function rejectRedemption(id: string, reason?: string): Promise<ActionResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/redemptions/${id}/reject`, { method: 'POST', headers, body: JSON.stringify(reason ? { reason } : {}) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to reject redemption' }));
    throw new Error(err.error ?? 'Failed to reject redemption');
  }
  return res.json();
}

export async function batchApproveRedemptions(ids: string[]): Promise<BatchApproveResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/redemptions/batch-approve`, { method: 'POST', headers, body: JSON.stringify({ ids }) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to batch-approve' }));
    throw new Error(err.error ?? 'Failed to batch-approve');
  }
  return res.json();
}
